import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Star, Wallet, Save, X, Pencil } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { vendorApi } from '@/services/api';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, useConfirm } from '@nepal-ecom/ui';

interface BankAccount {
  id: string;
  bankName: string;
  accountName: string;
  accountNumber: string;
  branchName?: string;
  isDefault: boolean;
}

interface DraftAccount {
  bankName: string;
  accountName: string;
  accountNumber: string;
  branchName: string;
  isDefault: boolean;
}

const EMPTY_DRAFT: DraftAccount = {
  bankName: '',
  accountName: '',
  accountNumber: '',
  branchName: '',
  isDefault: false,
};

export function BankAccounts() {
  const confirm = useConfirm();
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<DraftAccount>(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [edit, setEdit] = useState<DraftAccount>(EMPTY_DRAFT);

  useEffect(() => {
    let cancelled = false;
    vendorApi
      .getMyBankAccounts()
      .then((res) => {
        if (cancelled) return;
        const list = (res.data?.data ?? res.data) as BankAccount[];
        setAccounts(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error('Could not load bank accounts', {
          description: e?.response?.data?.message ?? String(e),
        });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const refresh = (next: BankAccount[]) => setAccounts(next);

  const validateDraft = (d: DraftAccount) => {
    if (!d.bankName.trim()) return 'Bank name is required';
    if (!d.accountName.trim()) return 'Account holder name is required';
    if (!d.accountNumber.trim()) return 'Account number is required';
    return null;
  };

  const submitAdd = async () => {
    const err = validateDraft(draft);
    if (err) return toast.error(err);
    setBusyId('new');
    try {
      const res = await vendorApi.addMyBankAccount({
        bankName: draft.bankName.trim(),
        accountName: draft.accountName.trim(),
        accountNumber: draft.accountNumber.trim(),
        branchName: draft.branchName.trim() || undefined,
        isDefault: draft.isDefault,
      });
      refresh((res.data?.data ?? res.data) as BankAccount[]);
      setDraft(EMPTY_DRAFT);
      setAdding(false);
      toast.success('Bank account added');
    } catch (e: any) {
      toast.error('Could not add account', { description: e?.response?.data?.message ?? String(e) });
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (a: BankAccount) => {
    setEditingId(a.id);
    setEdit({
      bankName: a.bankName,
      accountName: a.accountName,
      accountNumber: a.accountNumber,
      branchName: a.branchName ?? '',
      isDefault: a.isDefault,
    });
  };

  const submitEdit = async () => {
    if (!editingId) return;
    const err = validateDraft(edit);
    if (err) return toast.error(err);
    setBusyId(editingId);
    try {
      const res = await vendorApi.updateMyBankAccount(editingId, {
        bankName: edit.bankName.trim(),
        accountName: edit.accountName.trim(),
        accountNumber: edit.accountNumber.trim(),
        branchName: edit.branchName.trim() || undefined,
        isDefault: edit.isDefault,
      });
      refresh((res.data?.data ?? res.data) as BankAccount[]);
      setEditingId(null);
      toast.success('Bank account updated');
    } catch (e: any) {
      toast.error('Update failed', { description: e?.response?.data?.message ?? String(e) });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (a: BankAccount) => {
    if (accounts.length <= 1) {
      return toast.error('You must keep at least one bank account on file');
    }
    if (!(await confirm({
      title: `Remove ${a.bankName}?`,
      description: `Account ending …${a.accountNumber.slice(-4)} will be removed from your payout accounts.`,
      confirmLabel: 'Remove',
      tone: 'danger',
    }))) return;
    setBusyId(a.id);
    try {
      const res = await vendorApi.removeMyBankAccount(a.id);
      refresh((res.data?.data ?? res.data) as BankAccount[]);
      toast.success('Bank account removed');
    } catch (e: any) {
      toast.error('Could not remove account', { description: e?.response?.data?.message ?? String(e) });
    } finally {
      setBusyId(null);
    }
  };

  const setDefault = async (a: BankAccount) => {
    if (a.isDefault) return;
    setBusyId(a.id);
    try {
      const res = await vendorApi.setDefaultMyBankAccount(a.id);
      refresh((res.data?.data ?? res.data) as BankAccount[]);
      toast.success(`${a.bankName} is now your default payout account`);
    } catch (e: any) {
      toast.error('Could not set default', { description: e?.response?.data?.message ?? String(e) });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        title="Bank accounts"
        description="Manage where we send your weekly payouts. Pick one as the default."
        actions={
          !adding && (
            <Button onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4" /> Add account
            </Button>
          )
        }
      />

      {adding && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Wallet className="h-4 w-4 text-primary-600" /> New bank account
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BankAccountFields value={draft} onChange={setDraft} />
            <label className="mt-3 inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={draft.isDefault}
                onChange={(e) => setDraft({ ...draft, isDefault: e.target.checked })}
                className="h-4 w-4"
              />
              Set as default for payouts
            </label>
            <div className="flex gap-2 mt-4">
              <Button onClick={submitAdd} disabled={busyId === 'new'}>
                {busyId === 'new' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save account
              </Button>
              <Button variant="outline" onClick={() => { setAdding(false); setDraft(EMPTY_DRAFT); }}>
                <X className="h-4 w-4" /> Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="py-12 text-center text-sm text-slate-500">Loading…</div>
      ) : accounts.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <Wallet className="h-8 w-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-semibold text-slate-700">No bank accounts yet</p>
            <p className="text-xs text-slate-500 mt-1">Add a bank account so we can pay you.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {accounts.map((a) => (
            <Card key={a.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-primary-600" />
                    {a.bankName}
                  </CardTitle>
                  {a.isDefault && (
                    <Badge variant="success">
                      <Star className="h-3 w-3 mr-1 fill-current" /> Default
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {editingId === a.id ? (
                  <>
                    <BankAccountFields value={edit} onChange={setEdit} />
                    <div className="flex gap-2 mt-4">
                      <Button onClick={submitEdit} disabled={busyId === a.id}>
                        {busyId === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Save changes
                      </Button>
                      <Button variant="outline" onClick={() => setEditingId(null)}>
                        <X className="h-4 w-4" /> Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <dl className="text-sm space-y-1.5">
                      <Row label="Account holder" value={a.accountName} />
                      <Row label="Account #" value={a.accountNumber} mono />
                      {a.branchName && <Row label="Branch" value={a.branchName} />}
                    </dl>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {!a.isDefault && (
                        <Button
                          variant="outline"
                          onClick={() => setDefault(a)}
                          disabled={busyId === a.id}
                        >
                          <Star className="h-4 w-4" /> Make default
                        </Button>
                      )}
                      <Button variant="outline" onClick={() => startEdit(a)}>
                        <Pencil className="h-4 w-4" /> Edit
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => remove(a)}
                        disabled={busyId === a.id || accounts.length <= 1}
                        title={accounts.length <= 1 ? 'Keep at least one account on file' : undefined}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" /> Remove
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className={`text-sm text-slate-900 ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  );
}

function BankAccountFields({
  value, onChange,
}: {
  value: DraftAccount;
  onChange: (v: DraftAccount) => void;
}) {
  const set = (patch: Partial<DraftAccount>) => onChange({ ...value, ...patch });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Bank name">
        <Input
          value={value.bankName}
          onChange={(e) => set({ bankName: e.target.value })}
          placeholder="e.g. Nabil Bank"
        />
      </Field>
      <Field label="Branch (optional)">
        <Input
          value={value.branchName}
          onChange={(e) => set({ branchName: e.target.value })}
          placeholder="e.g. Kathmandu Main"
        />
      </Field>
      <Field label="Account holder name">
        <Input
          value={value.accountName}
          onChange={(e) => set({ accountName: e.target.value })}
          placeholder="Name as on the account"
        />
      </Field>
      <Field label="Account number">
        <Input
          value={value.accountNumber}
          onChange={(e) => set({ accountNumber: e.target.value })}
          placeholder="Account number"
          className="font-mono"
        />
      </Field>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
